import { ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import StickyCTABar from "@/components/StickyCTABar";
import MobileBottomBar from "@/components/navigation/MobileBottomBar";
import { captureFirstTouchAttribution } from "@/lib/leadAttribution";
import { trackEventStandalone } from "@/hooks/useAnalytics";

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const { pathname } = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    captureFirstTouchAttribution();
  }, []);

  useEffect(() => {
    const trackContactClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement).closest<HTMLAnchorElement>("a[href]");
      if (!anchor) return;
      if (anchor.href.startsWith("tel:")) {
        trackEventStandalone("phone_click", { page_path: pathname, contact_method: "phone" });
      } else if (anchor.href.startsWith("mailto:")) {
        trackEventStandalone("email_click", { page_path: pathname, contact_method: "email" });
      }
    };
    document.addEventListener("click", trackContactClick);
    return () => document.removeEventListener("click", trackContactClick);
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
      >
        Skip to main content
      </a>
      <Header />
      <main id="main-content" className="flex-1 pb-20 md:pb-0">{children}</main>
      <Footer />
      <ExitIntentPopup />
      <StickyCTABar />
      <MobileBottomBar />
    </div>
  );
};

export default Layout;
